import type { CancellationReason, Subscription, SubscriptionChange } from '../api/subscriptions'
import type { SubscriptionTier } from '../api/types'
import { type PlanId, planById } from '../checkout/catalogue'
import { addMonths, isAfterToday, toIsoDay } from './dates'

/**
 * A child's subscription as the portal reads it: what to call the plan, what
 * a household owner may do to it, and which of a child's subscriptions the
 * card shows.
 */

/** The checkout plan each tier is sold as, so the portal names plans exactly as the checkout does. */
const PLAN_BY_TIER: Record<SubscriptionTier, PlanId> = { kit: 'kit-studio', digital: 'online-only' }

export function planName(tier: SubscriptionTier): string {
  return planById(PLAN_BY_TIER[tier])?.name ?? tier
}

/** "$34.95 a month, plus tax and shipping" — once Recharge has said what it charges. */
export function priceLine(subscription: Subscription): string | null {
  if (subscription.price === null) return null
  const period = subscription.cadence === 'annual' ? 'a year' : 'a month'
  const extras = subscription.tier === 'kit' ? 'plus tax and shipping' : 'plus tax'
  return `${subscription.price} ${period}, ${extras}`
}

/** What the next charge brings, as the schedule row's label. */
export function scheduleLabel(subscription: Subscription): string {
  if (subscription.cadence === 'annual') return 'Renews'
  return subscription.tier === 'kit' ? 'Next kit' : 'Next payment'
}

/** What the next charge brings, as the start of a sentence: "The next kit ships and $34.95 is charged". */
export function chargeDescription(subscription: Subscription): string {
  const amount = subscription.price ?? 'the subscription'
  return subscription.tier === 'kit'
    ? `The next kit ships and ${amount} is charged`
    : `${amount} is charged`
}

/**
 * The day a pause form opens on: a month after the next charge, so the
 * default skips exactly one, or a month from today when nothing is scheduled.
 */
export function defaultResumeDay(subscription: Subscription, today = new Date()): string {
  const from =
    subscription.nextChargeOn && isAfterToday(subscription.nextChargeOn, today)
      ? subscription.nextChargeOn
      : toIsoDay(today)
  return addMonths(from, 1)
}

/**
 * Whether Recharge has reported the subscription. Until it has, the API has
 * neither a price nor a charge date for it and refuses every change, so the
 * card shows it as not active yet. Recharge keeps a price for a cancelled
 * subscription, so the price is the tell, not the charge date.
 */
export function knownToRecharge(subscription: Subscription): boolean {
  return subscription.price !== null
}

/**
 * The subscriptions a child's card shows: every live one, else the newest
 * cancelled one so it can be restarted, else none. The API lists newest first.
 */
export function subscriptionsToShow(subscriptions: Subscription[]): Subscription[] {
  const live = subscriptions.filter((subscription) => subscription.status !== 'cancelled')
  if (live.length > 0) return live
  const newest = subscriptions[0]
  return newest ? [newest] : []
}

/**
 * What a household owner may do from here. Skipping and pausing are for
 * monthly plans only: Recharge treats an annual plan as one charge a year, so
 * skipping would skip the renewal and resuming a pause would charge the whole
 * year tomorrow. Only a kit ships, so only a kit can be skipped or readdressed.
 */
export interface SubscriptionActions {
  skip: boolean
  pause: boolean
  resume: boolean
  restart: boolean
  cancel: boolean
  changeAddress: boolean
}

export function actionsFor(subscription: Subscription): SubscriptionActions {
  const { tier, cadence, status } = subscription
  const active = status === 'active'
  const monthly = cadence === 'monthly'
  const kit = tier === 'kit'
  return {
    skip: active && monthly && kit,
    pause: active && monthly,
    resume: status === 'paused',
    restart: status === 'cancelled',
    cancel: status !== 'cancelled',
    changeAddress: status !== 'cancelled' && kit,
  }
}

/**
 * What a change did to the subscription's status, in a word — Paused,
 * Cancelled, Resumed or Restarted — or null for a change that leaves the
 * status as it is, which the row that asked for it reports itself.
 */
export function changeOutcome(
  subscription: Subscription,
  change: SubscriptionChange,
): string | null {
  if (!('status' in change) || change.status === subscription.status) return null
  switch (change.status) {
    case 'paused':
      return 'Paused'
    case 'cancelled':
      return 'Cancelled'
    case 'active':
      return subscription.status === 'paused' ? 'Resumed' : 'Restarted'
  }
}

/** Why a parent cancels, worded from their side of the conversation. */
export function cancellationReasonLabel(
  reason: CancellationReason,
  childFirstName: string,
): string {
  switch (reason) {
    case 'too_expensive':
      return 'It’s too expensive'
    case 'not_using':
      return 'We’re not using it'
    case 'child_lost_interest':
      return `${childFirstName} lost interest`
    case 'moving':
      return 'We’re moving'
    case 'other':
      return 'Something else'
  }
}
